import { useRef, useState } from "react";
import { toast } from "sonner";
import { GripVertical, ImagePlus, Loader2, Plus, Trash2, X } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { adminApi } from "@/admin/lib";
import { errorMessage, mediaUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

export const PageTitle = ({ title, subtitle, children }) => (
  <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
    <div>
      <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl" data-testid="admin-page-title">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-body">{subtitle}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Card = ({ title, action, children, className, testId }) => (
  <section className={cn("rounded-2xl border border-line bg-white p-5 shadow-soft sm:p-6", className)} data-testid={testId}>
    {(title || action) && <div className="mb-4 flex items-center justify-between gap-3"><h2 className="font-heading text-base font-bold text-navy">{title}</h2>{action}</div>}
    {children}
  </section>
);

const btnBase = "inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-[background-color,transform] duration-150 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60";
export const YBtn = ({ className, loading, children, testId, ...p }) => (
  <button className={cn(btnBase, "bg-cta text-navy hover:bg-cta-hover", className)} data-testid={testId} disabled={loading || p.disabled} {...p}>
    {loading && <Loader2 className="h-4 w-4 animate-spin" />}{children}
  </button>
);
export const OBtn = ({ className, children, testId, ...p }) => (
  <button className={cn(btnBase, "border border-ocean bg-white font-semibold text-ocean hover:bg-[#F0F7FC]", className)} data-testid={testId} {...p}>{children}</button>
);
export const IconBtn = ({ className, children, testId, label, ...p }) => (
  <button type="button" aria-label={label} title={label} className={cn("grid h-8 w-8 place-items-center rounded-lg text-muted2 transition-colors hover:bg-page hover:text-ocean", className)} data-testid={testId} {...p}>{children}</button>
);

export const Field = ({ label, hint, children, className }) => (
  <label className={cn("block", className)}>
    <span className="label-base">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-xs text-muted2">{hint}</span>}
  </label>
);

export const Input = ({ className, testId, ...p }) => <input className={cn("input-base py-2.5", className)} data-testid={testId} {...p} />;
export const Area = ({ className, testId, rows = 3, ...p }) => <textarea rows={rows} className={cn("input-base py-2.5", className)} data-testid={testId} {...p} />;
export const Select = ({ options, testId, className, ...p }) => (
  <select className={cn("input-base py-2.5", className)} data-testid={testId} {...p}>
    {options.map((o) => (typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
  </select>
);
export const Toggle = ({ label, checked, onChange, testId, hint }) => (
  <div className="flex items-center justify-between gap-4 rounded-xl border border-line px-4 py-3">
    <div><p className="text-sm font-semibold text-navy">{label}</p>{hint && <p className="text-xs text-muted2">{hint}</p>}</div>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testId} />
  </div>
);

export const Confirm = ({ title = "Delete this item?", description = "This action cannot be undone.", onConfirm, children, actionLabel = "Delete" }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
    <AlertDialogContent className="rounded-2xl bg-white">
      <AlertDialogHeader>
        <AlertDialogTitle className="font-heading text-navy">{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="rounded-full" data-testid="confirm-cancel-btn">Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="rounded-full bg-red-600 text-white hover:bg-red-700" data-testid="confirm-delete-btn">{actionLabel}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const DeleteBtn = ({ onConfirm, testId, title }) => (
  <Confirm onConfirm={onConfirm} title={title}>
    <IconBtn label="Delete" className="hover:text-red-600" testId={testId}><Trash2 className="h-4 w-4" /></IconBtn>
  </Confirm>
);

export const Skel = ({ rows = 5 }) => (
  <div className="space-y-3" data-testid="admin-skeleton">{Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-xl bg-line/70" />)}</div>
);

const PILL = {
  new: "bg-sky/15 text-ocean", contacted: "bg-[#FEF3C7] text-[#92400E]", converted: "bg-[#DCFCE7] text-success", closed: "bg-line text-muted2",
  paid: "bg-[#DCFCE7] text-success", created: "bg-line text-muted2", failed: "bg-red-100 text-red-700", refunded: "bg-[#FEF3C7] text-[#92400E]",
  pending: "bg-[#FEF3C7] text-[#92400E]", done: "bg-[#DCFCE7] text-success", open: "bg-sky/15 text-ocean", resolved: "bg-[#DCFCE7] text-success",
  published: "bg-[#DCFCE7] text-success", draft: "bg-line text-muted2",
};
export const Pill = ({ value, testId }) => (
  <span className={cn("inline-flex rounded-full px-2.5 py-1 text-xs font-bold capitalize", PILL[String(value).toLowerCase()] || "bg-line text-muted2")} data-testid={testId}>{value}</span>
);

export const useDragSort = (items, setItems) => {
  const from = useRef(null);
  return (i) => ({
    draggable: true,
    onDragStart: () => (from.current = i),
    onDragOver: (e) => e.preventDefault(),
    onDrop: (e) => {
      e.preventDefault();
      const f = from.current;
      if (f === null || f === i) return;
      const next = [...items];
      const [m] = next.splice(f, 1);
      next.splice(i, 0, m);
      from.current = null;
      setItems(next);
    },
  });
};

export const StringList = ({ value = [], onChange, placeholder = "Add item", testId }) => {
  const [draft, setDraft] = useState("");
  const drag = useDragSort(value, onChange);
  const add = () => { if (draft.trim()) { onChange([...value, draft.trim()]); setDraft(""); } };
  return (
    <div className="space-y-2" data-testid={testId}>
      {value.map((v, i) => (
        <div key={i} {...drag(i)} className="flex items-center gap-2 rounded-xl border border-line bg-white px-2 py-1.5">
          <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-muted2" />
          <input className="flex-1 bg-transparent text-sm text-navy outline-none" value={v} onChange={(e) => onChange(value.map((x, k) => (k === i ? e.target.value : x)))} />
          <IconBtn label="Remove" onClick={() => onChange(value.filter((_, k) => k !== i))}><X className="h-4 w-4" /></IconBtn>
        </div>
      ))}
      <div className="flex gap-2">
        <Input value={draft} placeholder={placeholder} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} testId={testId && `${testId}-input`} />
        <OBtn type="button" onClick={add} className="px-4" testId={testId && `${testId}-add-btn`}><Plus className="h-4 w-4" /></OBtn>
      </div>
    </div>
  );
};

export const ObjectList = ({ value = [], onChange, fields, newItem, testId, addLabel = "Add" }) => {
  const drag = useDragSort(value, onChange);
  const set = (i, k, v) => onChange(value.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-3" data-testid={testId}>
      {value.map((it, i) => (
        <div key={i} {...drag(i)} className="flex gap-2 rounded-xl border border-line bg-page/60 p-3">
          <GripVertical className="mt-2 h-4 w-4 shrink-0 cursor-grab text-muted2" />
          <div className="grid flex-1 gap-2 sm:grid-cols-2">
            {fields.map((f) => f.type === "textarea"
              ? <Area key={f.key} rows={2} className="sm:col-span-2" placeholder={f.label} value={it[f.key] ?? ""} onChange={(e) => set(i, f.key, e.target.value)} />
              : <Input key={f.key} type={f.type || "text"} placeholder={f.label} value={it[f.key] ?? ""} onChange={(e) => set(i, f.key, f.type === "number" ? Number(e.target.value) : e.target.value)} />)}
          </div>
          <IconBtn label="Remove" onClick={() => onChange(value.filter((_, j) => j !== i))} className="hover:text-red-600"><Trash2 className="h-4 w-4" /></IconBtn>
        </div>
      ))}
      <OBtn type="button" onClick={() => onChange([...value, { ...newItem }])} testId={testId && `${testId}-add-btn`}><Plus className="h-4 w-4" />{addLabel}</OBtn>
    </div>
  );
};

export const uploadFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await adminApi.post("/admin/media/upload", fd);
  return data;
};

export const ImageField = ({ value, onChange, testId }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const m = await uploadFile(file);
      onChange(m.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <div className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-xl border border-line bg-page">
        {value ? <img src={mediaUrl(value)} alt="" className="h-full w-full object-cover" /> : <ImagePlus className="h-5 w-5 text-muted2" />}
      </div>
      <div className="flex-1 space-y-2">
        <Input value={value || ""} placeholder="Image URL or upload" onChange={(e) => onChange(e.target.value)} testId={testId && `${testId}-url`} />
        <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-bold text-ocean">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}Upload image
          <input type="file" accept="image/*" className="hidden" onChange={pick} data-testid={testId && `${testId}-file`} />
        </label>
      </div>
    </div>
  );
};

export const useSaver = () => {
  const [saving, setSaving] = useState(false);
  const run = async (fn, msg = "Saved") => {
    setSaving(true);
    try {
      const r = await fn();
      toast.success(msg);
      return r;
    } catch (e) {
      toast.error(errorMessage(e));
      return null;
    } finally {
      setSaving(false);
    }
  };
  return [saving, run];
};
